import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Stack, type Theme } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";

import { type ResolvedCancellationProtectionPayout } from "../api/types";
import { getTotalPayoutAmountInMinorUnits } from "../api/utils";

interface PayoutShiftCardProps {
  payout: ResolvedCancellationProtectionPayout;
}

export function PayoutShiftCard(props: PayoutShiftCardProps) {
  const { payout } = props;

  const originalPayoutAmount = payout.originalPayRate.amountInMinorUnits * payout.originalPaidHours;
  const totalPayoutAmount = getTotalPayoutAmountInMinorUnits(payout);

  return (
    <Stack
      aria-label="Payout shift card"
      sx={(theme: Theme) => ({
        borderRadius: theme.borderRadius?.medium,
        border: `${theme.borderWidth?.regular} solid ${theme.palette.background.secondary}`,
        overflow: "hidden",
        width: "100%",
      })}
    >
      {/* Top section - shift details and breakdown */}
      <Stack
        spacing={7}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.background.tertiary,
          padding: 5,
          borderTopLeftRadius: theme.borderRadius?.medium,
          borderTopRightRadius: theme.borderRadius?.medium,
        })}
      >
        {/* Shift info */}
        {(payout.shift?.start && payout.shift?.end) || payout.workplace?.name ? (
          <Stack spacing={1}>
            {payout.shift?.start && payout.shift?.end && (
              <Stack direction="row" spacing={2}>
                <Text
                  semibold
                  variant="body2"
                  sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                >
                  {formatShortDateWithWeekday(payout.shift.start, {
                    timeZone: payout.workplace?.tmz,
                  })}
                </Text>
                <Text
                  semibold
                  variant="body2"
                  sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                >
                  {formatTime(payout.shift.start, { timeZone: payout.workplace?.tmz })} -{" "}
                  {formatTime(payout.shift.end, { timeZone: payout.workplace?.tmz })}
                </Text>
              </Stack>
            )}
            {payout.workplace?.name && (
              <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                {payout.workplace.name}
              </Text>
            )}
          </Stack>
        ) : null}

        {/* Payout breakdown */}
        <Stack spacing={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
              <Text
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
              >
                {payout.originalPaidHours}hr cancellation payout
              </Text>
              <Text
                semibold
                variant="caption"
                sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}
              >
                ({formatCentsAsUsd(payout.originalPayRate.amountInMinorUnits)}/hr x{" "}
                {payout.originalPaidHours}hr)
              </Text>
            </Stack>
            <Text
              semibold
              variant="body2"
              sx={(theme: Theme) => ({ color: theme.palette.text.secondary, flexShrink: 0 })}
            >
              {formatCentsAsUsd(originalPayoutAmount)}
            </Text>
          </Stack>

          {payout.bonusAmount.amountInMinorUnits > 0 && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
              <Text
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
              >
                Additional payout
              </Text>
              <Text
                semibold
                variant="body2"
                sx={(theme: Theme) => ({ color: theme.palette.text.secondary, flexShrink: 0 })}
              >
                + {formatCentsAsUsd(payout.bonusAmount.amountInMinorUnits)}
              </Text>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Bottom section - total */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={4}
        sx={(theme: Theme) => ({
          backgroundColor: theme.palette.background.secondary,
          paddingX: 5,
          paddingY: 5,
          borderBottomLeftRadius: theme.borderRadius?.medium,
          borderBottomRightRadius: theme.borderRadius?.medium,
        })}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CbhIcon type="parachute" size="small" />
          <Text variant="body1" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
            Cancellation Protection
          </Text>
        </Stack>
        <Text
          semibold
          variant="h6"
          sx={(theme: Theme) => ({
            color: theme.palette.text.primary,
            flexShrink: 0,
          })}
        >
          {formatCentsAsUsd(totalPayoutAmount)}
        </Text>
      </Stack>
    </Stack>
  );
}
